import type { MoneyString } from '@/lib/money';

export type ContractParty = {
  id: number;
  party_id: number;
  party_name: string | null;
  role: string;
  is_primary: boolean;
};

export type Contract = {
  id: number;
  title: string;
  number: string | null;
  direction: 'receita' | 'despesa';
  status: string;
  entity_id: number;
  entity_name: string | null;

  starts_at: string;
  ends_at: string | null;
  /** Negativo = já passou. Nulo = prazo indeterminado. */
  days_to_expiry: number | null;
  in_notice_window: boolean;
  auto_renew: boolean;
  renewal_months: number | null;
  notice_days: number | null;

  amount: MoneyString;
  currency: string;
  frequency: string;
  due_day: number | null;
  billing_advance_days: number | null;
  last_billed_at: string | null;
  next_billing_at: string | null;

  adjustment_index: string | null;
  adjustment_percentage: number | null;
  adjustment_months: number | null;
  next_adjustment_at: string | null;
  due_for_adjustment: boolean;

  category_id: number | null;
  primary_party_id: number | null;
  primary_party_name: string | null;
  parties: ContractParty[];

  /** Nulo em prazo indeterminado — não há total a somar. */
  total_committed: MoneyString | null;
  /** O que este contrato já gerou em títulos, em dinheiro. */
  billed_total: MoneyString;

  /** Quantos títulos este contrato já gerou. */
  billed_count: number;
  /** Falso quando já faturou — aí o caminho é encerrar, não excluir. */
  can_delete: boolean;
};

export type ContractDetail = Contract & {
  description: string | null;
  notes: string | null;
  adjustments: Array<{
    applied_at: string;
    index: string | null;
    percentage: string | null;
    from: MoneyString;
    to: MoneyString;
    note: string | null;
  }>;
  renewals: Array<{ renewed_at: string; from: string; to: string }>;
  transactions: Array<{
    id: number;
    description: string;
    due_date: string;
    amount: MoneyString;
    settled: MoneyString;
    status: string;
  }>;
};

export type ContractListResponse = {
  data: Contract[];
  meta: {
    active_count: number;
    monthly_recurring_income: MoneyString;
    monthly_recurring_expense: MoneyString;
    monthly_total: MoneyString;
    expiring_soon: number;
    due_for_adjustment: number;
  };
};

export const FREQUENCIES: Array<{ id: string; label: string }> = [
  { id: 'mensal', label: 'Mensal' },
  { id: 'bimestral', label: 'Bimestral' },
  { id: 'trimestral', label: 'Trimestral' },
  { id: 'semestral', label: 'Semestral' },
  { id: 'anual', label: 'Anual' },
  { id: 'unica', label: 'Parcela única' },
];

export const ADJUSTMENT_INDEXES: Array<{ id: string; label: string; hint: string }> = [
  { id: 'nenhum', label: 'Sem reajuste', hint: 'O valor não muda. Nenhum alerta será enviado.' },
  { id: 'fixo', label: 'Percentual fixo', hint: 'Você define o percentual no contrato.' },
  { id: 'IPCA', label: 'IPCA', hint: 'O sistema avisa a data; você informa o índice acumulado.' },
  { id: 'IGPM', label: 'IGP-M', hint: 'O sistema avisa a data; você informa o índice acumulado.' },
  { id: 'INPC', label: 'INPC', hint: 'O sistema avisa a data; você informa o índice acumulado.' },
];

export const STATUS_STYLE: Record<string, { chip: string; label: string }> = {
  ativo: { chip: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300', label: 'Ativo' },
  rascunho: { chip: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300', label: 'Rascunho' },
  suspenso: { chip: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300', label: 'Suspenso' },
  encerrado: { chip: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400', label: 'Encerrado' },
  cancelado: { chip: 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300', label: 'Cancelado' },
  renovado: { chip: 'bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300', label: 'Renovado' },
};
